import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription } from "./ui/alert";
import { Star, MessageCircle, ThumbsUp, ThumbsDown, Send, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const ProtocolFeedback = ({ protocol, protocolId }) => {
  const [activeTab, setActiveTab] = useState('question'); // 'question' | 'feedback'
  const [question, setQuestion] = useState('');
  const [questionLoading, setQuestionLoading] = useState(false);
  const [questionResponse, setQuestionResponse] = useState(null);
  
  const [rating, setRating] = useState(0);
  const [improvements, setImprovements] = useState([]);
  const [sideEffects, setSideEffects] = useState('');
  const [additionalFeedback, setAdditionalFeedback] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const improvementOptions = [
    'Increased Energy',
    'Better Sleep',
    'Weight Loss',
    'Improved Recovery',
    'Enhanced Focus',
    'Reduced Inflammation',
    'Better Mood',
    'Improved Athletic Performance'
  ];

  const askQuestionAboutProtocol = async () => {
    if (!question.trim()) return;

    setQuestionLoading(true);
    setQuestionResponse(null);

    try {
      // Create context about the protocol for Dr. Peptide
      const protocolContext = `
I have a question about my generated protocol:

Protocol Summary:
- Primary Peptides: ${protocol?.primary_peptides?.join(', ') || 'N/A'}
- Clinical Reasoning: ${protocol?.clinical_reasoning?.substring(0, 200) || 'N/A'}...

My Question: ${question}`;

      const response = await axios.post(`${API}/dr-peptide/chat`, {
        message: protocolContext,
        conversation_history: []
      });

      if (response.data.success) {
        setQuestionResponse(response.data.response);
      } else {
        setQuestionResponse('Sorry, I encountered an issue. Please try again.');
      }
    } catch (error) {
      console.error('Question error:', error);
      setQuestionResponse('Failed to get response. Please try again.');
    } finally {
      setQuestionLoading(false);
    }
  };

  const toggleImprovement = (improvement) => {
    setImprovements(prev => 
      prev.includes(improvement)
        ? prev.filter(i => i !== improvement)
        : [...prev, improvement]
    );
  };

  const submitProtocolFeedback = async () => {
    if (rating === 0) {
      alert('Please provide a rating');
      return;
    }

    setFeedbackLoading(true);

    try {
      const feedbackData = {
        protocol_id: protocolId || 'unknown',
        feedback_data: {
          protocol_effectiveness: rating,
          specific_outcomes: {
            improvements_noticed: improvements,
            side_effects: sideEffects
          },
          additional_feedback: additionalFeedback
        }
      };

      const response = await axios.post(`${API}/dr-peptide/feedback`, feedbackData);

      if (response.data.success) {
        setFeedbackSubmitted(true);
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setFeedbackLoading(false);
    }
  };

  return (
    <Card className="mt-6 border-2 border-blue-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-blue-600" />
          Protocol Feedback & Questions
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Tab Selection */}
          <div className="flex gap-2">
            <Button
              variant={activeTab === 'question' ? 'default' : 'outline'}
              onClick={() => setActiveTab('question')}
              className="flex-1"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Ask Questions
            </Button>
            <Button
              variant={activeTab === 'feedback' ? 'default' : 'outline'}
              onClick={() => setActiveTab('feedback')}
              className="flex-1"
            >
              <Star className="h-4 w-4 mr-2" />
              Give Feedback
            </Button>
          </div>

          {/* Ask Questions Tab */}
          {activeTab === 'question' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="question">Ask Dr. Peptide about this protocol:</Label>
                <Textarea
                  id="question"
                  placeholder="Example: Why was BPC-157 chosen for my protocol? Can I take this with my current medications? What results should I expect in the first month?"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  rows={4}
                  className="mt-2"
                />
              </div>

              <Button
                onClick={askQuestionAboutProtocol}
                disabled={questionLoading || !question.trim()}
                className="w-full"
              >
                {questionLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Dr. Peptide is thinking...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Ask Dr. Peptide
                  </>
                )}
              </Button>

              {questionResponse && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <MessageCircle className="h-5 w-5 text-blue-600 mt-1" />
                      <div className="flex-1">
                        <p className="font-semibold text-blue-900 mb-2">Dr. Peptide's Answer:</p>
                        <div className="prose prose-sm max-w-none">
                          <div className="whitespace-pre-wrap text-gray-700">
                            {questionResponse}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Give Feedback Tab */}
          {activeTab === 'feedback' && (
            <div className="space-y-4">
              {feedbackSubmitted ? (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    <p className="font-semibold mb-2">Thank you for your feedback!</p>
                    <p>Your input helps Dr. Peptide improve and provide better recommendations for everyone.</p>
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  {/* Rating */}
                  <div>
                    <Label>How effective has this protocol been for you?</Label>
                    <div className="flex gap-2 mt-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setRating(star)}
                          className="focus:outline-none transition-transform hover:scale-110"
                        >
                          <Star
                            className={`h-8 w-8 ${
                              star <= rating
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        </button>
                      ))}
                      {rating > 0 && (
                        <span className="ml-2 text-sm text-gray-600 self-center">
                          {rating === 1 && 'Not Effective'}
                          {rating === 2 && 'Somewhat Effective'}
                          {rating === 3 && 'Moderately Effective'}
                          {rating === 4 && 'Very Effective'}
                          {rating === 5 && 'Extremely Effective'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Improvements Noticed */}
                  <div>
                    <Label>What improvements have you noticed? (Select all that apply)</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {improvementOptions.map((improvement) => (
                        <Badge
                          key={improvement}
                          variant={improvements.includes(improvement) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => toggleImprovement(improvement)}
                        >
                          {improvement}
                          {improvements.includes(improvement) && (
                            <CheckCircle className="h-3 w-3 ml-1" />
                          )}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Side Effects */}
                  <div>
                    <Label htmlFor="sideEffects">Any side effects experienced? (Optional)</Label>
                    <Textarea
                      id="sideEffects"
                      placeholder="Describe any side effects you've experienced..."
                      value={sideEffects}
                      onChange={(e) => setSideEffects(e.target.value)}
                      rows={3}
                      className="mt-2"
                    />
                  </div>

                  {/* Additional Feedback */}
                  <div>
                    <Label htmlFor="additionalFeedback">Additional feedback (Optional)</Label>
                    <Textarea
                      id="additionalFeedback"
                      placeholder="Any other feedback about this protocol?"
                      value={additionalFeedback}
                      onChange={(e) => setAdditionalFeedback(e.target.value)}
                      rows={3}
                      className="mt-2"
                    />
                  </div>

                  <Button
                    onClick={submitProtocolFeedback}
                    disabled={feedbackLoading || rating === 0}
                    className="w-full"
                    size="lg"
                  >
                    {feedbackLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <ThumbsUp className="h-4 w-4 mr-2" />
                        Submit Feedback
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProtocolFeedback;
